import { isProxy, isRef, toRaw, unref } from 'vue';

/**
 * Recursively unwrap Vue reactivity (reactive proxies, refs) into plain
 * structured-cloneable data. postMessage throws DataCloneError on a Vue
 * Proxy, so every value crossing a worker boundary must pass through this.
 * Dates, ArrayBuffers and typed arrays pass through untouched; Map/Set are
 * rebuilt with converted entries. Handles shared/circular references.
 */
export function deepToRaw(value) {
  return convert(value, new WeakMap());
}

function convert(value, seen) {
  if (isRef(value)) value = unref(value);
  if (value === null || typeof value !== 'object') return value;

  const raw = isProxy(value) ? toRaw(value) : value;
  if (seen.has(raw)) return seen.get(raw);

  if (raw instanceof Date || raw instanceof ArrayBuffer || ArrayBuffer.isView(raw)) {
    return raw;
  }

  if (raw instanceof Map) {
    const out = new Map();
    seen.set(raw, out);
    raw.forEach((entryValue, key) => out.set(convert(key, seen), convert(entryValue, seen)));
    return out;
  }

  if (raw instanceof Set) {
    const out = new Set();
    seen.set(raw, out);
    raw.forEach(entry => out.add(convert(entry, seen)));
    return out;
  }

  if (Array.isArray(raw)) {
    const out = new Array(raw.length);
    seen.set(raw, out);
    for (let i = 0; i < raw.length; i++) out[i] = convert(raw[i], seen);
    return out;
  }

  const out = {};
  seen.set(raw, out);
  for (const key of Object.keys(raw)) out[key] = convert(raw[key], seen);
  return out;
}
